import { reactive } from 'vue';
import api from '../api';
import { outbox, kv } from './db';

/**
 * Optional "work offline, sync later" feature — enabled per install.
 *
 * When enabled and the device can't reach the server, a sale is stored in a
 * local outbox (IndexedDB) and replayed to the API automatically once the
 * connection returns. When disabled, everything behaves exactly as before.
 */
const LS_ENABLED = 'offline_mode';

export const offline = reactive({
  enabled: (typeof localStorage !== 'undefined' && localStorage.getItem(LS_ENABLED) === '1'),
  online: (typeof navigator === 'undefined') ? true : navigator.onLine !== false,
  pending: 0,
  failed: 0,
  syncing: false,
  lastSync: null,
});

export function isEnabled() { return offline.enabled; }
export function isWorkingOffline() { return offline.enabled && (!offline.online || (typeof navigator !== 'undefined' && !navigator.onLine)); }

export function setEnabled(on) {
  offline.enabled = !!on;
  try { localStorage.setItem(LS_ENABLED, on ? '1' : '0'); } catch (e) { /* ignore */ }
  if (on) refreshCounts();
}

export function setOnline(on) {
  const was = offline.online;
  offline.online = !!on;
  if (!was && on && offline.enabled) sync();
}

export async function refreshCounts() {
  try {
    const all = await outbox.all();
    offline.pending = all.filter(i => i.status !== 'failed').length;
    offline.failed = all.filter(i => i.status === 'failed').length;
  } catch (e) { offline.pending = 0; offline.failed = 0; }
}

/** Queue a sale body for later sync. Returns a local reference string. */
export async function queueSale(body) {
  const ref = 'OFF-' + Date.now();
  await outbox.add({ type: 'sale', body, ref });
  await refreshCounts();
  return ref;
}

/** List queued/failed items for the settings UI. */
export function listQueue() { return outbox.all(); }
export function removeQueued(id) { return outbox.remove(id).then(refreshCounts); }

/** Cache reference data (products, customers…) for offline use. */
export function cache(key, value) { return kv.set(key, value).catch(() => {}); }
export function cached(key) { return kv.get(key).catch(() => null); }

/** Replay every pending item to the server. Safe to call repeatedly. */
export async function sync() {
  if (offline.syncing || !offline.enabled) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  offline.syncing = true;
  try {
    const items = await outbox.all();
    for (const item of items) {
      if (item.status === 'failed') continue;
      try {
        if (item.type === 'sale') await api.post('/save_sales', item.body);
        await outbox.remove(item.id);
      } catch (err) {
        if (err && err.response) {
          // Server rejected it (e.g. stock changed) — keep it, flag for review,
          // never silently drop a money transaction.
          await outbox.update(item.id, { status: 'failed', error: (err.response.data && err.response.data.message) || 'Rejected by server' });
        } else {
          // Network error — stop, we're offline again; retry later.
          offline.online = false;
          break;
        }
      }
    }
    offline.lastSync = Date.now();
  } finally {
    await refreshCounts();
    offline.syncing = false;
  }
}

export function initOffline() {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => setOnline(true));
  window.addEventListener('offline', () => setOnline(false));
  // api.js dispatches this when a request fails with no HTTP response.
  window.addEventListener('api-network-error', () => { offline.online = false; });
  window.addEventListener('api-online', () => { if (!offline.online) setOnline(true); });
  if (offline.enabled) { refreshCounts(); if (offline.online) sync(); }
}
